// @ts-check
import {useImageUpload} from '@/hooks/useImageUpload'
import {deleteBrandImage} from '@/services/ProductsService/ProductCatalog/productCatalog'
import {useMutation} from '@tanstack/react-query'
import {useCallback, useState} from 'react'

const getDeleteErrorMessage = error => {
  return error?.data?.message || error?.message || 'Failed to delete brand image.'
}

/**
 * @param {{
 *   brandId: string,
 *   setBrands: import('react').Dispatch<import('react').SetStateAction<import('@/types/productCatalogTypes').ProductCategoryBrand[]>>
 * }} options
 */
export const useBrandImage = ({brandId, setBrands}) => {
  const {
    upload,
    uploading,
    error: uploadError,
    progress,
    clearError,
  } = useImageUpload({id: brandId, entityType: 'displaybrand'})
  const [displayError, setDisplayError] = useState(/** @type {string | null} */ (null))

  const {mutate: deleteImage, isPending: isDeleting} = useMutation({
    mutationFn: () => deleteBrandImage(brandId),
    onSuccess: () => {
      setDisplayError(null)
      setBrands(prev => prev.map(b => (b.id === brandId ? {...b, imageUrl: undefined} : b)))
    },
    onError: error => {
      setDisplayError(getDeleteErrorMessage(error))
    },
  })

  const handleUpload = useCallback(
    async file => {
      try {
        setDisplayError(null)
        const result = await upload(file)
        if (result) {
          setBrands(prev => prev.map(b => (b.id === brandId ? {...b, imageUrl: result.url} : b)))
        }
      } catch (err) {
        console.error('Error uploading image:', err)
      }
    },
    [upload, brandId, setBrands]
  )

  const handleDelete = useCallback(() => {
    clearError()
    setDisplayError(null)
    deleteImage()
  }, [clearError, deleteImage])

  const error = displayError || uploadError

  return {
    handleUpload,
    handleDelete,
    uploading: uploading || isDeleting,
    error,
    progress,
  }
}
